import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ChatbotWidget.scss';
import { getRequest, postRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL } from 'Includes/urls';

// ─── Helpers ────────────────────────────────────────────────────────────────

function getLoggedInUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function resolveUserType(user) {
  if (!user) return 'student';
  // Edubricz staff have no company set
  if (!user.company && !user.company_id) return 'staff';
  return 'student';
}

function formatTime(date) {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Renders **bold** markdown in bot messages
function BotText({ text }) {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return (
    <span>
      {parts.map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>
      )}
    </span>
  );
}

// ─── Quick reply suggestions per user type ──────────────────────────────────

const QUICK_REPLIES = {
  student: [
    '🔑 Forgot my password',
    '💰 Fee payment query',
    '📅 Class schedule',
    '📄 Need a document',
    '🎓 Admission query',
  ],
  staff: [
    '🔑 Reset user password',
    '💳 Billing issue',
    '⚙️ System not working',
    '👤 User management',
    '📊 Report problem',
  ],
};

// ─── Main Component ──────────────────────────────────────────────────────────

function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [userType, setUserType] = useState('student');
  const [unreadCount, setUnreadCount] = useState(0);
  const [greetingLoaded, setGreetingLoaded] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // ── Resolve user type on mount ──
  useEffect(() => {
    const user = getLoggedInUser();
    const type = resolveUserType(user);
    setUserType(type);
  }, []);

  // ── Auto-scroll to latest message ──
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // ── Focus input when panel opens ──
  useEffect(() => {
    if (open) {
      setUnreadCount(0);
      setTimeout(() => inputRef.current?.focus(), 150);
      if (!greetingLoaded) {
        loadGreeting();
      }
    }
  }, [open]);

  // ── Load greeting from backend ──
  const loadGreeting = useCallback(() => {
    const url = GET_URL.chatbot?.api;
    if (!url) {
      // Fallback if URL not available yet
      const greeting =
        userType === 'staff'
          ? "👋 Hello! I'm the **Edubricz Support Assistant**.\n\nHow can I help you today?"
          : "👋 Hi! I'm your **Edubricz Support Bot**.\n\nHow can I help you today?";
      appendBotMessage(greeting);
      setGreetingLoaded(true);
      return;
    }

    getRequest(url, { user_type: userType }, {})
      .then((res) => {
        if (res && res.status === 200) {
          appendBotMessage(res.data.greeting || 'Hello! How can I help you?');
          setUserType(res.data.user_type || userType);
        } else {
          appendBotMessage('👋 Hi! How can I help you today?');
        }
      })
      .catch(() => {
        appendBotMessage('👋 Hi! How can I help you today?');
      })
      .finally(() => setGreetingLoaded(true));
  }, [userType]);

  // ── Append helpers ──
  const appendBotMessage = (text, extra = {}) => {
    setMessages((prev) => [
      ...prev,
      { role: 'bot', text, time: new Date(), ...extra },
    ]);
  };

  const appendUserMessage = (text) => {
    setMessages((prev) => [
      ...prev,
      { role: 'user', text, time: new Date() },
    ]);
  };

  // ── Send a message ──
  const sendMessage = useCallback(
    (text) => {
      const trimmed = text.trim();
      if (!trimmed || isTyping) return;

      appendUserMessage(trimmed);
      setInputValue('');
      setIsTyping(true);

      const url = POST_URL.chatbot?.api;
      if (!url) {
        // Fallback mock
        setTimeout(() => {
          setIsTyping(false);
          appendBotMessage(
            "I'm sorry, the chatbot service is temporarily unavailable. Please try the Help Center instead."
          );
        }, 1000);
        return;
      }

      postRequest(url, { message: trimmed, user_type: userType }, {})
        .then((res) => {
          setIsTyping(false);
          if (res && res.status === 200) {
            const { bot_reply, ticket_created, ticket_id } = res.data;
            appendBotMessage(bot_reply, {
              ticketCreated: ticket_created,
              ticketId: ticket_id,
            });
            // Increment unread badge if panel is closed
            if (!open) setUnreadCount((c) => c + 1);
          } else {
            appendBotMessage(
              "I'm having trouble right now. Please try again in a moment."
            );
          }
        })
        .catch(() => {
          setIsTyping(false);
          appendBotMessage(
            "I'm having trouble connecting. Please check your network and try again."
          );
        });
    },
    [isTyping, userType, open]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleQuickReply = (text) => {
    sendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const quickReplies = QUICK_REPLIES[userType] || QUICK_REPLIES.student;
  const showQuickReplies = messages.length <= 1;

  return (
    <>
      {/* ── Floating Action Button ── */}
      <button
        className="chatbot-fab"
        onClick={() => setOpen((o) => !o)}
        aria-label="Open support chatbot"
        title="Support Chat"
      >
        {open ? '✕' : '💬'}
        {!open && unreadCount > 0 && (
          <span className="chatbot-fab-badge">{unreadCount}</span>
        )}
      </button>

      {/* ── Chat Panel ── */}
      {open && (
        <div className="chatbot-panel" role="dialog" aria-label="Support Chatbot">

          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-avatar">🤖</div>
            <div className="chatbot-header-info">
              <div className="chatbot-header-name">Edubricz Support</div>
              <div className="chatbot-header-status">Online</div>
            </div>
            <button
              className="chatbot-close-btn"
              onClick={() => setOpen(false)}
              aria-label="Close chatbot"
            >
              ✕
            </button>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`chatbot-msg chatbot-msg--${msg.role}`}
              >
                {msg.role === 'bot' && (
                  <div className="chatbot-msg-avatar">🤖</div>
                )}
                <div>
                  <div className="chatbot-msg-bubble">
                    {msg.role === 'bot' ? (
                      <>
                        <BotText text={msg.text} />
                        {msg.ticketCreated && msg.ticketId && (
                          <div className="chatbot-ticket-badge">
                            🎫 Ticket #{msg.ticketId} created
                          </div>
                        )}
                      </>
                    ) : (
                      msg.text
                    )}
                  </div>
                  <div className="chatbot-msg-time">
                    {formatTime(msg.time)}
                  </div>
                </div>
              </div>
            ))}

            {/* Typing dots */}
            {isTyping && (
              <div className="chatbot-typing">
                <span /><span /><span />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Reply Chips */}
          {showQuickReplies && (
            <div className="chatbot-quick-replies">
              {quickReplies.map((chip, i) => (
                <button
                  key={i}
                  className="chatbot-chip"
                  onClick={() => handleQuickReply(chip.replace(/^[^\w]+ ?/, ''))}
                >
                  {chip}
                </button>
              ))}
            </div>
          )}

          {/* Input Bar */}
          <form className="chatbot-input-bar" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              className="chatbot-input"
              type="text"
              placeholder="Type your message…"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isTyping}
              autoComplete="off"
            />
            <button
              type="submit"
              className="chatbot-send-btn"
              disabled={!inputValue.trim() || isTyping}
              aria-label="Send message"
            >
              ➤
            </button>
          </form>
        </div>
      )}
    </>
  );
}

export default ChatbotWidget;
